import React, { useState, useEffect } from 'react';
import themeLayoutMappingApi from '@services/themeLayoutMappingApi.jsx';
import ResourceUploadField from '@components/common/ResourceUploadField.jsx';

const ThemeLayoutSelectionModal = ({ 
    themeId, 
    availableLayouts = [], 
    onClose, 
    onSave, 
    isEditMode = false, 
    initialData = null 
}) => {
    // 상태 관리
    const [selectedLayoutId, setSelectedLayoutId] = useState(null);
    const [customDefaultImageResourceId, setCustomDefaultImageResourceId] = useState(null);
    const [customHoverImageResourceId, setCustomHoverImageResourceId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedLayoutInfo, setSelectedLayoutInfo] = useState(null);

    // 초기 데이터 설정 (수정 모드)
    useEffect(() => {
        if (isEditMode && initialData) {
            console.log('수정 모드 초기 데이터:', initialData);
            
            setSelectedLayoutId(initialData.photoLayoutId);
            setCustomDefaultImageResourceId(initialData.customDefaultImageResourceId);
            setCustomHoverImageResourceId(initialData.customHoverImageResourceId);
            
            // 수정 모드에서는 현재 선택된 레이아웃 정보를 직접 사용
            if (initialData.layoutName) {
                setSelectedLayoutInfo({
                    id: initialData.photoLayoutId,
                    layoutName: initialData.layoutName,
                    layoutType: initialData.layoutType,
                    layoutDirection: initialData.layoutDirection,
                    layoutTypeDescription: initialData.layoutTypeDescription,
                    layoutDirectionDescription: initialData.layoutDirectionDescription,
                    isDeployed: initialData.isDeployed
                });
            } else {
                // availableLayouts에서 찾기 시도
                const layoutInfo = availableLayouts.find(layout => layout.id === initialData.photoLayoutId);
                if (layoutInfo) {
                    setSelectedLayoutInfo(layoutInfo);
                }
            }
        }
    }, [isEditMode, initialData, availableLayouts]);

    // 디버깅을 위한 상태 추적
    useEffect(() => {
        console.log('현재 상태:', {
            selectedLayoutId,
            customDefaultImageResourceId,
            customHoverImageResourceId,
            selectedLayoutInfo
        });
    }, [selectedLayoutId, customDefaultImageResourceId, customHoverImageResourceId, selectedLayoutInfo]);

    // 레이아웃 선택 핸들러
    const handleLayoutSelect = (layoutId) => {
        setSelectedLayoutId(layoutId);
        const layoutInfo = availableLayouts.find(layout => layout.id === layoutId);
        setSelectedLayoutInfo(layoutInfo);
    };

    // 리소스 업로드 핸들러
    const handleResourceUploaded = (resourceType, resourceId) => {
        console.log(`리소스 업로드: ${resourceType} = ${resourceId}`);
        if (resourceType === 'customDefaultImage') {
            setCustomDefaultImageResourceId(resourceId);
        } else if (resourceType === 'customHoverImage') {
            setCustomHoverImageResourceId(resourceId);
        }
    };

    // 방향 표시 헬퍼
    const getDirectionDisplay = (direction) => {
        switch (direction) {
            case 'horizontal':
                return '가로';
            case 'vertical':
                return '세로';
            default:
                return '-';
        }
    };

    // 저장 핸들러
    const handleSave = async () => {
        if (!isEditMode && !selectedLayoutId) {
            alert('레이아웃을 선택해주세요.');
            return;
        }

        setIsLoading(true);
        try {
            let result;
            
            if (isEditMode) {
                // 수정 모드
                result = await themeLayoutMappingApi.updateThemeLayoutMapping(
                    initialData.id, 
                    {
                        customDefaultImageResourceId,
                        customHoverImageResourceId
                    }
                );
            } else {
                // 추가 모드
                result = await themeLayoutMappingApi.addLayoutToTheme(
                    themeId,
                    selectedLayoutId,
                    {
                        defaultImageResourceId: customDefaultImageResourceId,
                        hoverImageResourceId: customHoverImageResourceId
                    }
                );
            }

            onSave(result);
            onClose();
        } catch (error) {
            console.error('저장 실패:', error);
            alert(`저장 중 오류가 발생했습니다: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="modal-overlay active">
                <div className="modal-container theme-layout-modal">
                    <div className="modal-header">
                        <h3 className="modal-title">
                            테마 레이아웃 {isEditMode ? '수정' : '선택'}
                        </h3>
                        <button className="modal-close" onClick={onClose}>
                            <i className="ri-close-line"></i>
                        </button>
                    </div>
                    
                    <div className="modal-body">
                        <div className="modal-content">
                            {/* 레이아웃 선택 섹션 (추가 모드에서만) */}
                            {!isEditMode && (
                                <div className="modal-form-group">
                                    <label className="modal-label">레이아웃 선택</label>
                                    <div className="layout-selection-grid">
                                        {availableLayouts.map((layout) => (
                                            <div 
                                                key={layout.id}
                                                className={`layout-selection-item ${selectedLayoutId === layout.id ? 'selected' : ''}`}
                                                onClick={() => handleLayoutSelect(layout.id)}
                                            >
                                                <div className="layout-info">
                                                    <h4>{layout.layoutName}</h4>
                                                    <div className="layout-details">
                                                        <span className="layout-type">{layout.layoutType || '-'}</span>
                                                        <span className="layout-direction">{getDirectionDisplay(layout.layoutDirection)}</span>
                                                    </div>
                                                </div>
                                                {/*{layout.layoutDataResourceId && (
                                                    <div className="layout-preview">
                                                        <img 
                                                            src={`/api/v1/resources/${layout.layoutDataResourceId}/view`} 
                                                            alt={layout.layoutName}
                                                            onError={(e) => {
                                                                e.target.src = '/images/layoutData/default_layout.png';
                                                            }}
                                                        />
                                                    </div>
                                                )}*/}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* 선택된 레이아웃 정보 표시 */}
                            {selectedLayoutInfo && (
                                <div className="modal-form-group">
                                    <label className="modal-label">선택된 레이아웃 정보</label>
                                    <div className="selected-layout-info">
                                        <div className="layout-details">
                                            <h4>{selectedLayoutInfo.layoutName}</h4>
                                            <div className="layout-meta">
                                                <span className="layout-type">{selectedLayoutInfo.layoutType || '-'}</span>
                                                <span className="layout-direction">{getDirectionDisplay(selectedLayoutInfo.layoutDirection)}</span>
                                                <span className="layout-deployed">{selectedLayoutInfo.isDeployed ? '배포중' : '미배포'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 커스텀 이미지 업로드 섹션 */}
                            <div className="modal-form-group">
                                <div className="modal-row-container">
                                    <div className="modal-column">
                                        <label className="modal-label">테마별 기본 이미지</label>
                                        <ResourceUploadField
                                            label=""
                                            resourceId={customDefaultImageResourceId}
                                            onResourceUploaded={(id) => 
                                                handleResourceUploaded('customDefaultImage', id)
                                            }
                                            acceptTypes="image/*"
                                            showPreview={true}
                                        />
                                        <div className="help-text">
                                            레이아웃 선택 시 기본적으로 표시될 이미지입니다.
                                        </div>
                                    </div>
                                    
                                    <div className="modal-column">
                                        <label className="modal-label">테마별 호버 이미지</label>
                                        <ResourceUploadField
                                            label=""
                                            resourceId={customHoverImageResourceId}
                                            onResourceUploaded={(id) => 
                                                handleResourceUploaded('customHoverImage', id)
                                            }
                                            acceptTypes="image/*"
                                            showPreview={true}
                                        />
                                        <div className="help-text">
                                            마우스 오버 시 표시될 이미지입니다.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="modal-footer">
                        <button 
                            className="modal-btn modal-btn-cancel" 
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            취소
                        </button>
                        <button 
                            className="modal-btn modal-btn-primary" 
                            onClick={handleSave}
                            disabled={isLoading || (!isEditMode && !selectedLayoutId)}
                        >
                            {isLoading ? '저장 중...' : (isEditMode ? '수정' : '추가')}
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                .theme-layout-modal {
                    max-width: 900px;
                    width: 90%;
                    /* 애니메이션 충돌 방지 */
                    animation: none !important;
                    transform: none !important;
                }

                .layout-selection-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
                    gap: 15px;
                    margin-top: 10px;
                    max-height: 400px;
                    overflow-y: auto;
                }

                .layout-selection-item {
                    border: 2px solid #e0e0e0;
                    border-radius: 8px;
                    padding: 15px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    background: #fff;
                    /* 레이아웃 시프트 방지 */
                    min-height: 160px;
                    display: flex;
                    flex-direction: column;
                }

                .layout-selection-item:hover {
                    border-color: #007bff;
                    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
                }

                .layout-selection-item.selected {
                    border-color: #007bff;
                    background: #f8f9ff;
                    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
                }

                .layout-info {
                    flex-shrink: 0;
                    margin-bottom: 10px;
                }

                .layout-info h4 {
                    margin: 0 0 8px 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #333;
                }

                .layout-details {
                    display: flex;
                    gap: 8px;
                    flex-wrap: wrap;
                }

                .layout-type, .layout-direction {
                    font-size: 12px;
                    color: #666;
                    background: #f0f0f0;
                    padding: 2px 8px;
                    border-radius: 12px;
                }

                .layout-preview {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .layout-preview img {
                    width: 100%;
                    max-height: 100px;
                    object-fit: cover;
                    border-radius: 4px;
                    border: 1px solid #e0e0e0;
                    /* 이미지 로딩 중 레이아웃 유지 */
                    background-color: #f5f5f5;
                    display: block;
                }

                .selected-layout-info {
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    padding: 15px;
                }

                .selected-layout-info .layout-details h4 {
                    margin: 0 0 8px 0;
                    font-size: 18px;
                    color: #333;
                }

                .layout-meta {
                    display: flex;
                    gap: 8px;
                    flex-wrap: wrap;
                }

                .layout-deployed {
                    font-size: 12px;
                    color: #28a745;
                    background: #e8f5e9;
                    padding: 2px 8px;
                    border-radius: 12px;
                }

                .help-text {
                    font-size: 12px;
                    color: #666;
                    margin-top: 5px;
                    font-style: italic;
                }
            `}</style>
        </>
    );
};

export default ThemeLayoutSelectionModal;
